import React, {
  memo,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
import SoftBox from 'components/SoftBox'
import styled from 'styled-components'
import { Box, Button, Grid, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import fehbLogo from 'assets/images/fehb.svg'
import { updateDeduction } from 'graphql/mutations'
import { apiClient } from 'utils/api/apiClient'

const StyledBenefitTile = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #fff;
  border: 1px solid #ddd;
  width: 100%;
  // height: 300px;
  border-radius: 8px;
  div {
    display: flex;
    justify-content: space-between;
    &.benefitInfo {
      border-top: 1px solid #ddd;
      > span:first-of-type {
        color: #bbb;
      }
    }
    &.iconSpace {
      min-height: 65px;
      padding: 12px;
    }
    flex-direction: row;
    width: 100%;
    span {
      width: 50%;
      padding: 0 10px;
      margin: 5px 0;
      text-align: left;
    }
  }
`

const StyledLogo = styled.img`
  max-height: 80px;
  width: auto;
  max-width: 100%;
`

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const Benefits = () => {
  const [deductions, setDeductions] = useState([])

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    const updatedDeductions = addLookupData(foundDeductions, 'planInfo')
    setDeductions(updatedDeductions)
  }, [])

  const { intakeState } = useContext(IntakeContext)

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  // console.log(deductions)

  const fehbLookup = intakeState.fehbCode
    ? intakeState.fehbCode.replace('HP ', '').match(/^\d/)
      ? '_' + intakeState.fehbCode.replace('HP ', '')
      : intakeState.fehbCode
    : ''
  const fehbData = selectData.fehb[fehbLookup]
  // console.log(intakeState?.healthInsurancePremium)

  // Add benefit information to appropriate deductions
  const tableAssignment = { fdvd: 'dental', fdvv: 'vision' }
  function lookupData(cost, type) {
    return selectData[type].find(
      (item) =>
        item.biweeklySelfPlus === cost ||
        item.biweeklySelf === cost ||
        item.biweeklyFamily === cost
    )
  }

  function addLookupData(array, key) {
    return array.map((obj) => ({
      ...obj,
      [key]:
        obj.subType === 'fdvd' || obj.subType === 'fdvv'
          ? lookupData(obj.amount, tableAssignment[obj.subType])
          : {},
    }))
  }

  const removedBenefits = [
    'alotchecking',
    'ltc',
    'uniondues',
    'charity',
    'alotsavings',
    'alotchecking',
    'alotother',
    'visiondental',
    'vbp',
    'extratax',
  ]

  const fsaData = {
    logo: 'https://media.defense.gov/2021/May/26/2002728768/700/465/0/210526-G-WC787-001.PNG',
    name: 'Federal Flexible Spending Account',
    website: 'https://fsafeds.com/',
  }

  const hsaData = {
    logo: 'https://www.opm.gov/cdn/img/opm-uswds/opm_logo.svg',
    name: 'Health Savings Account',
    website:
      'https://www.opm.gov/healthcare-insurance/healthcare/health-savings-accounts/',
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box mt={0} p={3}>
        <Box mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography>
                Your benefits details will display based on the information you
                have added to your profile!
              </Typography>
            </Grid>
            {intakeState.healthInsurance === 'fehb' && intakeState.fehbCode && (
              <Grid item xs={12} lg={6} xxl={4}>
                <StyledBenefitTile>
                  <div className="iconSpace">
                    <StyledLogo src={fehbLogo} />
                  </div>
                  <div className="benefitInfo">
                    <span>Plan Name</span>
                    <span>
                      {fehbData?.name === undefined
                        ? 'No Matching 2025 Plan'
                        : fehbData?.name}
                    </span>
                  </div>
                  <div className="benefitInfo">
                    <span>Plan Option</span>
                    <span>
                      {fehbData?.option === undefined
                        ? 'No Matching 2025 Option'
                        : fehbData?.option}
                    </span>
                  </div>
                  <div className="benefitInfo">
                    <span>Plan Type</span>
                    <span>
                      {fehbData?.type === undefined
                        ? 'No Matching 2025 Type'
                        : fehbData?.type}
                    </span>
                  </div>
                  <div className="benefitInfo">
                    <span>Benefit Summary</span>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: '12px',
                          backgroundColor: '#344767',
                          color: '#fff',
                          fontWeight: 'normal',
                          boxShadow: 'none',
                          padding: '0px 16px',
                          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                            transform: 'none',
                            backgroundColor: '#293852',
                            boxShadow: 'none',
                          },
                          '.MuiButton-endIcon': {
                            width: 'auto',
                            paddingRight: '0px',
                          },
                        }}
                        endIcon={<OpenInNewIcon />}
                        target="_blank"
                        href={
                          fehbData?.link === undefined
                            ? 'https://www.opm.gov/healthcare-insurance/healthcare/plan-information/summary-of-benefits/'
                            : fehbData?.link
                        }
                      >
                        View Plan
                      </Button>
                    </span>
                  </div>
                  <div className="benefitInfo">
                    <span>Premium</span>
                    <span>
                      {dollarFormat.format(
                        fehbData?.biweeklyEmp === undefined
                          ? intakeState?.healthInsurancePremium
                            ? intakeState?.healthInsurancePremium
                            : 0
                          : fehbData?.biweeklyEmp
                      )}{' '}
                      Per Pay Period
                      <br></br>
                      {dollarFormat.format(
                        ((fehbData?.biweeklyEmp === undefined
                          ? intakeState?.healthInsurancePremium
                            ? intakeState?.healthInsurancePremium
                            : 0
                          : fehbData?.biweeklyEmp) *
                          26) /
                          12
                      )}{' '}
                      Per Month
                    </span>
                  </div>
                </StyledBenefitTile>
              </Grid>
            )}
            {deductions.map((benefit) =>
              removedBenefits.includes(benefit.subType) ? (
                <></>
              ) : benefit.subType === 'fsa' ? (
                <Grid item xs={12} lg={6} xxl={4}>
                  <StyledBenefitTile>
                    <div className="iconSpace">
                      <StyledLogo src={fsaData.logo} />
                    </div>
                    <div className="benefitInfo">
                      <span>Plan Name</span>
                      <span>{fsaData.name}</span>
                    </div>
                    <div className="benefitInfo">
                      <span>Benefit Information</span>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            fontSize: '12px',
                            backgroundColor: '#344767',
                            color: '#fff',
                            fontWeight: 'normal',
                            boxShadow: 'none',
                            padding: '0px 16px',
                            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                              transform: 'none',
                              backgroundColor: '#293852',
                              boxShadow: 'none',
                            },
                            '.MuiButton-endIcon': {
                              width: 'auto',
                              paddingRight: '0px',
                            },
                          }}
                          endIcon={<OpenInNewIcon />}
                          target="_blank"
                          href={fsaData.website}
                        >
                          View Plan
                        </Button>
                      </span>
                    </div>
                    <div className="benefitInfo">
                      <span>Premium</span>
                      <span>
                        {dollarFormat.format(benefit.amount)} Per Pay Period
                        <br></br>
                        {dollarFormat.format((benefit.amount * 26) / 12)} Per
                        Month
                      </span>
                    </div>
                  </StyledBenefitTile>
                </Grid>
              ) : benefit.subType === 'hsa' ? (
                <Grid item xs={12} lg={6} xxl={4}>
                  <StyledBenefitTile>
                    <div className="iconSpace">
                      <StyledLogo src={hsaData.logo} />
                    </div>
                    <div className="benefitInfo">
                      <span>Plan Name</span>
                      <span>{hsaData.name}</span>
                    </div>
                    <div className="benefitInfo">
                      <span>Benefit Information</span>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            fontSize: '12px',
                            backgroundColor: '#344767',
                            color: '#fff',
                            fontWeight: 'normal',
                            boxShadow: 'none',
                            padding: '0px 16px',
                            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                              transform: 'none',
                              backgroundColor: '#293852',
                              boxShadow: 'none',
                            },
                            '.MuiButton-endIcon': {
                              width: 'auto',
                              paddingRight: '0px',
                            },
                          }}
                          endIcon={<OpenInNewIcon />}
                          target="_blank"
                          href={hsaData.website}
                        >
                          View Plan
                        </Button>
                      </span>
                    </div>
                    <div className="benefitInfo">
                      <span>Premium</span>
                      <span>
                        {dollarFormat.format(benefit.amount)} Per Pay Period
                        <br></br>
                        {dollarFormat.format((benefit.amount * 26) / 12)} Per
                        Month
                      </span>
                    </div>
                  </StyledBenefitTile>
                </Grid>
              ) : (
                <Grid item xs={12} lg={6} xxl={4}>
                  <StyledBenefitTile>
                    {benefit?.planInfo ? (
                      <>
                        <div className="iconSpace">
                          <StyledLogo src={benefit.planInfo.logo} />
                        </div>
                        <div className="benefitInfo">
                          <span>Plan Name</span>
                          <span>{benefit.planInfo.name}</span>
                        </div>
                        <div className="benefitInfo">
                          <span>Benefit Summary</span>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                fontSize: '12px',
                                backgroundColor: '#344767',
                                color: '#fff',
                                fontWeight: 'normal',
                                boxShadow: 'none',
                                padding: '0px 16px',
                                '&:hover, &:focus, &:active, &:focus:not(:hover)':
                                  {
                                    transform: 'none',
                                    backgroundColor: '#293852',
                                    boxShadow: 'none',
                                  },
                                '.MuiButton-endIcon': {
                                  width: 'auto',
                                  paddingRight: '0px',
                                },
                              }}
                              endIcon={<OpenInNewIcon />}
                              target="_blank"
                              href={benefit.planInfo.website}
                            >
                              View Plan
                            </Button>
                          </span>
                        </div>
                        <div className="benefitInfo">
                          <span>Premium</span>
                          <span>
                            {dollarFormat.format(benefit.amount)} Per Pay Period
                            <br></br>
                            {dollarFormat.format(
                              (benefit.amount * 26) / 12
                            )}{' '}
                            Per Month
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="iconSpace">
                          <Typography
                            variant="h3"
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {tableAssignment[benefit.subType]} Plan
                          </Typography>
                        </div>
                        <div className="benefitInfo">
                          <span>Plan Name</span>
                          <span>No Matching Plan Based on Your Premium</span>
                        </div>
                        <div className="benefitInfo">
                          <span>Premium</span>
                          <span>
                            {dollarFormat.format(benefit.amount)} Per Pay Period
                            <br></br>
                            {dollarFormat.format(
                              (benefit.amount * 26) / 12
                            )}{' '}
                            Per Month
                          </span>
                        </div>
                      </>
                    )}
                  </StyledBenefitTile>
                </Grid>
              )
            )}
          </Grid>
        </Box>
      </Box>
    </DashboardLayout>
  )
}
